import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import { articles } from '@/mocks/data';
import { breadcrumbSchema, articleSchema } from '@/mocks/schema';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  useEffect(() => {
    if (article) {
      const scriptId = 'schema-article';
      const existing = document.getElementById(scriptId);
      if (existing) existing.remove();

      const breadcrumb = breadcrumbSchema([
        { name: '首页', url: 'https://91.n' },
        { name: '使用指南', url: 'https://91.n/article/guide' },
        { name: article.title, url: `https://91.n/article/${article.slug}` },
      ]);

      const articleData = articleSchema(article);

      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = scriptId;
      script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": [breadcrumb, articleData] });
      document.head.appendChild(script);

      return () => {
        const s = document.getElementById(scriptId);
        if (s) s.remove();
      };
    }
  }, [article]);

  if (!article) {
    return (
      <>
        <SEOHead title="文章" description="91.n 使用指南与帮助文章" />
        <ScrollToTop />
        <Navbar />
        <main className="pt-24 pb-16 bg-background-50 min-h-screen">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-file-unknow-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="text-2xl font-bold text-foreground-950 mb-2">文章未找到</h1>
            <p className="text-foreground-500 mb-6">您访问的文章不存在或已被移除</p>
            <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap">
              <i className="ri-arrow-left-line"></i> 返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles.filter((a) => a.id !== article.id).slice(0, 2);

  return (
    <>
      <SEOHead title={article.title} description={article.excerpt} />
      <ScrollToTop />
      <Navbar />

      <main className="pt-20 pb-16 bg-background-50 min-h-screen">
        <article className="max-w-3xl mx-auto px-4 md:px-6">
          <Breadcrumb items={[
            { label: '使用指南' },
            { label: article.title },
          ]} />

          <header className="mt-4 mb-8">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              {article.tags.map((tag) => (
                <span key={tag} className="px-3 py-1 bg-primary-100 text-primary-700 text-xs rounded-full font-medium">{tag}</span>
              ))}
              <span className="text-foreground-400 text-xs">{article.createdAt}</span>
              <span className="text-foreground-400 text-xs">·</span>
              <span className="text-foreground-400 text-xs">阅读 {article.readTime}</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-foreground-950 mb-4">{article.title}</h1>
            <p className="text-foreground-600 text-base leading-relaxed">{article.excerpt}</p>
          </header>

          <div className="prose prose-sm max-w-none">
            {article.content.split('\n').map((line, index) => {
              if (line.startsWith('## ')) {
                return <h2 key={index} className="text-2xl font-bold text-foreground-950 mt-8 mb-4">{line.replace('## ', '')}</h2>;
              }
              if (line.startsWith('### ')) {
                return <h3 key={index} className="text-xl font-semibold text-foreground-900 mt-6 mb-3">{line.replace('### ', '')}</h3>;
              }
              if (line.startsWith('1. **') || line.startsWith('2. **') || line.startsWith('3. **') || line.startsWith('4. **')) {
                const cleaned = line.replace(/^\d+\.\s*\*\*(.+?)\*\*[：:]?\s*/, '');
                const title = line.match(/\*\*(.+?)\*\*/)?.[1] || '';
                return (
                  <div key={index} className="flex items-start gap-2 mb-2 ml-2">
                    <span className="font-semibold text-foreground-900 text-sm mt-0.5">{title}：</span>
                    <span className="text-foreground-600 text-sm leading-relaxed">{cleaned}</span>
                  </div>
                );
              }
              if (line.startsWith('- **') || line.startsWith('- ')) {
                const cleaned = line.replace(/^-\s*\*\*(.+?)\*\*[：:]?\s*/, '').replace(/^- /, '');
                const title = line.match(/\*\*(.+?)\*\*/)?.[1] || '';
                return (
                  <div key={index} className="flex items-start gap-2 mb-2 ml-2">
                    {title && <span className="font-semibold text-foreground-900 text-sm mt-0.5">{title}：</span>}
                    <span className="text-foreground-600 text-sm leading-relaxed">{title ? cleaned : line.replace('- ', '')}</span>
                  </div>
                );
              }
              if (line.trim() === '') {
                return <div key={index} className="h-3"></div>;
              }
              return <p key={index} className="text-foreground-600 text-sm leading-relaxed mb-3">{line}</p>;
            })}
          </div>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <div className="mt-12 pt-8 border-t border-background-200/70">
              <h3 className="text-lg font-bold text-foreground-950 mb-4">相关文章</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.slug}`}
                    className="group bg-background-50 border border-background-200/70 rounded-xl p-5 hover:border-primary-300 transition-all duration-200"
                  >
                    <h4 className="font-semibold text-foreground-900 text-sm mb-2 group-hover:text-primary-500 transition-colors">{ra.title}</h4>
                    <p className="text-foreground-500 text-xs leading-relaxed line-clamp-2 mb-3">{ra.excerpt}</p>
                    <span className="text-primary-500 text-xs font-medium">阅读全文 →</span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Back to Home */}
          <div className="mt-8 text-center">
            <Link to="/" className="inline-flex items-center gap-2 text-sm text-foreground-500 hover:text-primary-500 transition-colors">
              <i className="ri-arrow-left-line"></i> 返回首页
            </Link>
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}